<template>
  <v-dialog
    v-model="dialog"
    :max-width="previewMode ? '1200px' : '700px'"
    persistent
    scrollable
  >
    <v-card>
      <!-- Header -->
      <v-toolbar flat class="elevation-0">
        <v-icon left color="primary">mdi-printer</v-icon>
        <v-toolbar-title class="text-h6">
          Configurar Impresión de Etiquetas
        </v-toolbar-title>
        <v-spacer />

        <!-- Estado basado en pruebas reales -->
        <v-chip
          :color="overallStatus.color"
          :text-color="overallStatus.textColor"
          small
          outlined
        >
          <v-icon left small>{{ overallStatus.icon }}</v-icon>
          {{ overallStatus.text }}
        </v-chip>

        <v-btn icon @click="togglePreview" :color="previewMode ? 'primary' : 'grey'">
          <v-icon>mdi-eye</v-icon>
        </v-btn>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Status de pruebas iniciales -->
      <v-alert v-if="initialTesting" type="info" dense class="ma-4">
        <v-row align="center">
          <v-col cols="auto">
            <v-progress-circular indeterminate size="20" width="2" />
          </v-col>
          <v-col> Probando métodos de impresión disponibles... </v-col>
        </v-row>
      </v-alert>

      <!-- Resultados de las pruebas -->
      <v-alert
        v-if="!initialTesting && testedMethods.length > 0"
        type="success"
        dense
        outlined
        class="ma-4"
      >
        <strong>Métodos probados y disponibles:</strong><br />
        <span class="text-caption">
          <span v-for="method in testedMethods" :key="method.id" class="mr-3">
            <v-icon small :color="method.color" class="mr-1">{{ method.icon }}</v-icon>
            {{ method.name }}
          </span>
        </span>
      </v-alert>

      <!-- Si no hay métodos probados exitosamente -->
      <v-alert
        v-if="!initialTesting && testedMethods.length === 0"
        type="warning"
        dense
        outlined
        class="ma-4"
      >
        <strong>Solo descarga disponible</strong><br />
        <span class="text-caption">
          No se detectaron métodos de impresión directa funcionales
        </span>
      </v-alert>

      <!-- Contenido Principal -->
      <v-card-text class="pa-0">
        <v-container fluid>
          <v-row :class="{ 'no-gutters': previewMode }">
            <v-col :cols="previewMode ? 5 : 12">
              <v-card flat class="pa-4">
                <!-- Método de Impresión - Solo métodos probados -->
                <div class="mb-4">
                  <v-subheader class="pl-0 text-subtitle-1 font-weight-medium">
                    <v-icon left>mdi-connection</v-icon>
                    Método de Impresión
                    <v-spacer />
                    <v-btn
                      x-small
                      outlined
                      color="primary"
                      :loading="retesting"
                      @click="retestMethods"
                    >
                      <v-icon left x-small>mdi-refresh</v-icon>
                      Re-probar
                    </v-btn>
                  </v-subheader>

                  <v-radio-group v-model="selectedMethod" column>
                    <!-- Descarga - Siempre disponible -->
                    <v-radio value="download">
                      <template v-slot:label>
                        <div class="d-flex align-center w-100">
                          <v-icon color="success" class="mr-2">mdi-download</v-icon>
                          <div class="flex-grow-1">
                            <div><strong>Descargar Archivo ZPL</strong></div>
                            <div class="text-caption grey--text">
                              Funciona en todos los navegadores - Descarga automática
                            </div>
                          </div>
                          <v-chip x-small color="success" outlined>
                            <v-icon left x-small>mdi-check</v-icon>
                            Probado
                          </v-chip>
                        </div>
                      </template>
                    </v-radio>

                    <!-- Métodos probados dinámicamente -->
                    <v-radio
                      v-for="method in testedMethods"
                      :key="method.id"
                      :value="method.id"
                    >
                      <template v-slot:label>
                        <div class="d-flex align-center w-100">
                          <v-icon :color="method.color" class="mr-2">{{
                            method.icon
                          }}</v-icon>
                          <div class="flex-grow-1">
                            <div>
                              <strong>{{ method.name }}</strong>
                            </div>
                            <div class="text-caption grey--text">
                              {{ method.description }}
                            </div>
                            <div
                              v-if="method.testDetails"
                              class="text-caption success--text"
                            >
                              ✓ {{ method.testDetails }}
                            </div>
                          </div>
                          <div class="d-flex flex-column align-end">
                            <v-chip x-small :color="method.color" outlined class="mb-1">
                              <v-icon left x-small>mdi-check</v-icon>
                              Funcional
                            </v-chip>
                            <v-btn
                              v-if="method.retestable"
                              icon
                              x-small
                              :loading="testingMethod === method.id"
                              @click.stop="retestSpecificMethod(method.id)"
                            >
                              <v-icon x-small>mdi-test-tube</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </div>

                <!-- Configuración específica solo para métodos probados -->
                <div v-if="selectedMethodNeedsConfig" class="mb-4">
                  <v-divider class="mb-3" />
                  <v-subheader class="pl-0 text-subtitle-1 font-weight-medium">
                    <v-icon left>mdi-cog</v-icon>
                    Configuración {{ getSelectedMethodName() }}
                  </v-subheader>

                  <!-- Config específica según el método probado -->
                  <div v-if="selectedMethod === 'network'">
                    <v-row dense>
                      <v-col cols="6">
                        <v-text-field
                          v-model="methodConfigs.network.ip"
                          label="IP de la Impresora"
                          outlined
                          dense
                          placeholder="192.168.1.100"
                        />
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model.number="methodConfigs.network.port"
                          label="Puerto"
                          type="number"
                          outlined
                          dense
                          placeholder="9100"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </div>

                <!-- Productos Seleccionados con cantidades individuales -->
                <div class="mb-4">
                  <v-subheader class="pl-0 text-subtitle-1 font-weight-medium">
                    <v-icon left>mdi-package-variant</v-icon>
                    Productos Seleccionados ({{ selectedItems.length }})
                  </v-subheader>

                  <v-list v-if="selectedItems.length > 0" dense>
                    <v-list-item
                      v-for="(item, index) in selectedItems"
                      :key="item.ean || item.refe || index"
                      class="px-0"
                    >
                      <v-list-item-content>
                        <v-list-item-title class="text-subtitle-2">
                          {{ truncateText(item.nombre || item.refe, 25) }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          Stock: {{ item.cantidad || 0 }} | Costo:
                          {{ formatPrice(item.costo || 0) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action class="flex-row align-center">
                        <v-text-field
                          v-model.number="item.cantidadImprimir"
                          type="number"
                          :min="1"
                          :max="50"
                          label="Cantidad"
                          outlined
                          dense
                          hide-details
                          style="width: 80px"
                          class="mr-2"
                          @input="generatePreview"
                        />
                        <v-btn icon small color="error" @click="removeItem(item)">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>

                  <v-alert v-else type="info" dense outlined class="mt-2">
                    No hay productos seleccionados
                  </v-alert>
                </div>

                <v-divider class="mb-4" />

                <!-- Configuración de papel y plantilla -->
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="selectedPaperSize"
                      :items="paperSizes"
                      item-text="name"
                      item-value="id"
                      label="Tamaño de Papel"
                      outlined
                      dense
                      @change="generatePreview"
                    >
                      <template v-slot:item="{ item }">
                        <div>
                          <div class="font-weight-medium">{{ item.name }}</div>
                          <div class="text-caption grey--text">
                            {{ item.width }}×{{ item.height }}mm
                            <span v-if="item.columns">
                              - {{ item.columns }} columnas</span
                            >
                            <v-chip
                              v-if="item.isCAB"
                              x-small
                              color="orange"
                              text-color="white"
                              class="ml-2"
                            >
                              CAB
                            </v-chip>
                          </div>
                        </div>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="selectedTemplate"
                      :items="availableTemplates"
                      item-text="name"
                      item-value="id"
                      label="Plantilla"
                      outlined
                      dense
                      @change="generatePreview"
                    />
                  </v-col>
                </v-row>

                <!-- Información específica para CAB Multi-columna -->
                <v-row v-if="isCABMultiColumn" dense class="mb-2">
                  <v-col cols="12">
                    <v-alert type="info" dense outlined>
                      <v-row align="center">
                        <v-col cols="auto">
                          <v-icon color="orange">mdi-view-column</v-icon>
                        </v-col>
                        <v-col>
                          <strong>Formato CAB Multi-columna</strong><br />
                          <span class="text-caption">
                            {{ currentPaperSize.columns }} etiquetas de
                            {{ currentPaperSize.width }}×{{ currentPaperSize.height }}mm
                            por fila - Optimizado para impresión masiva
                          </span>
                        </v-col>
                      </v-row>
                    </v-alert>
                  </v-col>
                </v-row>

                <!-- Opciones de contenido -->
                <v-row dense class="mb-4">
                  <v-col cols="6">
                    <v-checkbox
                      v-model="labelOptions.includePrice"
                      label="Incluir Precio"
                      dense
                      @change="generatePreview"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-checkbox
                      v-model="labelOptions.includeBarcode"
                      label="Código de Barras"
                      dense
                      @change="generatePreview"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-checkbox
                      v-model="labelOptions.includeDiscount"
                      label="Mostrar Descuento"
                      dense
                      @change="generatePreview"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-checkbox
                      v-if="isCABFormat"
                      v-model="labelOptions.includeLote"
                      label="Incluir Lote/Fecha"
                      dense
                      @change="generatePreview"
                    />
                  </v-col>
                  <v-col v-if="isCABMultiColumn" cols="6">
                    <v-select
                      v-model="labelOptions.fillOrder"
                      :items="fillOrderOptions"
                      label="Orden de Llenado"
                      outlined
                      dense
                      @change="generatePreview"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <!-- Panel de Preview -->
            <v-col v-if="previewMode" cols="7">
              <v-card flat class="preview-panel">
                <v-card-title class="pb-2">
                  <v-icon left>mdi-eye</v-icon>
                  Vista Previa
                  <v-spacer />
                  <v-chip small color="info" outlined>
                    {{ currentPaperSize.name }}
                  </v-chip>
                  <v-chip
                    v-if="isCABFormat"
                    small
                    color="orange"
                    text-color="white"
                    class="ml-2"
                  >
                    CAB {{ currentPaperSize.columns }}×Columnas
                  </v-chip>
                </v-card-title>

                <v-card-text class="preview-container">
                  <div
                    class="preview-scroll"
                    :class="{ 'cab-multi-column': isCABMultiColumn }"
                  >
                    <!-- Preview para formato multi-columna -->
                    <template v-if="isCABMultiColumn">
                      <div
                        v-for="(row, rowIndex) in previewRows"
                        :key="`row-${rowIndex}`"
                        class="label-row"
                        :style="getRowStyle()"
                      >
                        <div
                          v-for="(label, colIndex) in row"
                          :key="`label-${rowIndex}-${colIndex}`"
                          class="label-preview cab-column-label"
                          :style="getCABLabelStyle()"
                        >
                          <div class="label-content" v-html="label.htmlPreview" />
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip
                                x-small
                                color="grey"
                                outlined
                                class="label-info"
                                v-bind="attrs"
                                v-on="on"
                              >
                                {{ label.index }}
                              </v-chip>
                            </template>
                            <span>{{ label.productName }}</span>
                          </v-tooltip>
                        </div>
                      </div>
                    </template>

                    <!-- Preview para formato single -->
                    <template v-else>
                      <div
                        v-for="(item, index) in previewLabels"
                        :key="`preview-${index}`"
                        class="label-preview"
                        :style="getLabelStyle()"
                      >
                        <div class="label-content" v-html="item.htmlPreview" />

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              x-small
                              color="grey"
                              outlined
                              class="label-info"
                              v-bind="attrs"
                              v-on="on"
                            >
                              {{ index + 1 }}
                            </v-chip>
                          </template>
                          <span>{{ item.productName }}</span>
                        </v-tooltip>
                      </div>
                    </template>
                  </div>

                  <v-alert
                    v-if="previewLabels.length > 0"
                    color="info"
                    dense
                    outlined
                    class="mt-3"
                  >
                    <strong>Total:</strong> {{ totalLabels }} etiquetas ({{
                      selectedItems.length
                    }}
                    productos)
                    <div v-if="isCABMultiColumn" class="text-caption mt-1">
                      Distribuidas en {{ previewRows.length }} filas de
                      {{ currentPaperSize.columns }} columnas
                    </div>
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <!-- Acciones -->
      <v-card-actions class="px-6 py-4">
        <v-btn text @click="closeDialog">Cancelar</v-btn>

        <v-btn
          v-if="selectedMethod === 'download'"
          text
          color="info"
          @click="downloadZPL"
          :disabled="selectedItems.length === 0"
        >
          <v-icon left>mdi-download</v-icon>
          Descargar ZPL
        </v-btn>

        <v-spacer />

        <v-btn
          color="primary"
          @click="processLabels"
          :loading="printing"
          :disabled="selectedItems.length === 0"
        >
          <v-icon left>mdi-printer</v-icon>
          {{ getActionButtonText() }}
        </v-btn>
      </v-card-actions>

      <!-- Debug Panel -->
      <v-expansion-panels v-if="showDebug" flat>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-caption">Debug - Configuración CAB</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <pre class="text-caption">{{
              JSON.stringify(
                {
                  currentPaperSize,
                  isCABFormat,
                  isCABMultiColumn,
                  previewRows: previewRows.length,
                  labelOptions,
                },
                null,
                2
              )
            }}</pre>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "LabelPrinter",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      dialog: false,
      previewMode: false,
      printing: false,
      selectedItems: [],
      selectedMethod: "download",
      selectedPaperSize: "cab_33x25_3col", // Defaultear al formato CAB correcto
      selectedTemplate: "cab_compact",

      // Estados de pruebas
      initialTesting: false,
      retesting: false,
      testingMethod: null,
      testedMethods: [],
      testResults: {},

      labelOptions: {
        includePrice: true,
        includeBarcode: true,
        includeDiscount: false,
        includeLote: true, // Para formatos CAB
        fillOrder: "horizontal", // horizontal | vertical
      },

      methodConfigs: {
        network: {
          ip: "192.168.1.100",
          port: 9100,
          printerType: "CAB",
        },
        usb: {
          device: null,
        },
        serial: {
          port: null,
          baudRate: 9600,
        },
      },

      previewLabels: [],
      previewRows: [], // Para formato multi-columna
      showDebug: process.env.NODE_ENV === "development",

      // Formatos de papel corregidos
      paperSizes: [
        // Formatos Zebra existentes
        {
          id: "zebra_50x30",
          name: "Zebra 50×30mm",
          width: 50,
          height: 30,
          dpi: 203,
          pixelWidth: 400,
          pixelHeight: 240,
          isCAB: false,
          columns: 1,
        },
        {
          id: "zebra_70x40",
          name: "Zebra 70×40mm",
          width: 70,
          height: 40,
          dpi: 203,
          pixelWidth: 560,
          pixelHeight: 320,
          isCAB: false,
          columns: 1,
        },
        // Formato CAB 33x25 - 3 columnas (PROBADO Y FUNCIONAL)
        {
          id: "cab_33x25_3col",
          name: "CAB 33×25mm (3 columnas)",
          width: 33, // Ancho real de cada etiqueta
          height: 25,
          dpi: 300,
          pixelWidth: 390, // 33mm a 300 DPI
          pixelHeight: 295, // 25mm a 300 DPI
          isCAB: true,
          columns: 3,
          rollWidth: 108, // Ancho total del rollo
          printerType: "CAB",
          maxTextLines: 6,
          // Posiciones exactas probadas en impresora real
          columnPositions: [45, 325, 595],
          totalWidth: 1276, // ^PW1276 del script funcional
        },
        // Otros formatos CAB comunes
        {
          id: "cab_50x30_2col",
          name: "CAB 50×30mm (2 columnas)",
          width: 50,
          height: 30,
          dpi: 300,
          pixelWidth: 591,
          pixelHeight: 354,
          isCAB: true,
          columns: 2,
          rollWidth: 105,
          printerType: "CAB",
          maxTextLines: 5,
        },
      ],

      // Plantillas optimizadas para CAB
      templates: [
        {
          id: "basic",
          name: "Básica",
          description: "Nombre, precio y código de barras",
          compatibleWith: ["zebra", "cab"],
        },
        {
          id: "mini",
          name: "Minimalista",
          description: "Solo información esencial",
          compatibleWith: ["zebra", "cab"],
        },
        {
          id: "cab_compact",
          name: "CAB Compacta",
          description: "Optimizada para etiquetas CAB pequeñas",
          compatibleWith: ["cab"],
          features: ["compact", "multiColumn"],
        },
        {
          id: "cab_retail",
          name: "CAB Retail",
          description: "Para tiendas con precio destacado",
          compatibleWith: ["cab"],
          features: ["retail", "priceEmphasis"],
        },
      ],

      fillOrderOptions: [
        { text: "Horizontal (izq. a der.)", value: "horizontal" },
        { text: "Vertical (arriba a abajo)", value: "vertical" },
      ],
    };
  },

  computed: {
    currentPaperSize() {
      return (
        this.paperSizes.find((p) => p.id === this.selectedPaperSize) || this.paperSizes[0]
      );
    },

    isCABFormat() {
      return this.currentPaperSize.isCAB === true;
    },

    isCABMultiColumn() {
      return this.isCABFormat && this.currentPaperSize.columns > 1;
    },

    availableTemplates() {
      if (this.isCABFormat) {
        return this.templates.filter(
          (t) => !t.compatibleWith || t.compatibleWith.includes("cab")
        );
      }
      return this.templates.filter(
        (t) => !t.compatibleWith || t.compatibleWith.includes("zebra")
      );
    },

    totalLabels() {
      return this.selectedItems.reduce((total, item) => {
        return total + (item.cantidadImprimir || 0);
      }, 0);
    },

    overallStatus() {
      if (this.initialTesting) {
        return {
          color: "info",
          textColor: "white",
          icon: "mdi-loading",
          text: "Probando...",
        };
      }

      if (this.testedMethods.length > 0) {
        return {
          color: "success",
          textColor: "white",
          icon: "mdi-check-circle",
          text: `${this.testedMethods.length + 1} métodos`,
        };
      }

      return {
        color: "warning",
        textColor: "white",
        icon: "mdi-download",
        text: "Solo Descarga",
      };
    },

    selectedMethodNeedsConfig() {
      return (
        this.selectedMethod !== "download" &&
        this.testedMethods.find((m) => m.id === this.selectedMethod)
      );
    },
  },

  watch: {
    value(newVal) {
      this.dialog = newVal;
      if (newVal) {
        this.selectedItems = this.initializeItems([...this.items]);
        if (this.testedMethods.length === 0 && !this.initialTesting) {
          this.testAllMethods();
        }
        this.$nextTick(() => {
          this.generatePreview();
        });
      }
    },

    dialog(newVal) {
      this.$emit("input", newVal);
    },

    items: {
      handler(newItems) {
        this.selectedItems = this.initializeItems([...newItems]);
        this.generatePreview();
      },
      deep: true,
    },

    selectedPaperSize() {
      // Cambiar template automáticamente si es necesario
      if (this.isCABFormat && this.selectedTemplate === "basic") {
        this.selectedTemplate = "cab_compact";
      } else if (!this.isCABFormat && this.selectedTemplate.startsWith("cab_")) {
        this.selectedTemplate = "basic";
      }
    },
  },

  async mounted() {
    // No hacer nada hasta que el usuario abra el dialog
  },

  methods: {
    closeDialog() {
      this.dialog = false;
      this.previewMode = false;
    },

    togglePreview() {
      this.previewMode = !this.previewMode;
      if (this.previewMode) {
        this.$nextTick(() => {
          this.generatePreview();
        });
      }
    },

    // PRUEBAS DE MÉTODOS (mantener lógica existente simplificada)
    async testAllMethods() {
      this.initialTesting = true;
      this.testedMethods = [];

      console.log("🧪 Iniciando pruebas de métodos de impresión...");

      const tests = [
        this.testCABNetworkMethod(),
        this.testUSBMethod(),
        this.testSerialMethod(),
      ];

      await Promise.allSettled(tests);

      this.initialTesting = false;
      console.log(
        "✅ Pruebas completadas. Métodos funcionales:",
        this.testedMethods.length
      );
    },

    async testCABNetworkMethod() {
      try {
        console.log("🏭 Probando método de red CAB...");

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        try {
          const response = await fetch("http://localhost:3001/cab/health", {
            signal: controller.signal,
            method: "GET",
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            this.testedMethods.push({
              id: "network",
              name: "Impresora CAB (Servicio Local)",
              description: "Conexión TCP/IP optimizada para CAB",
              icon: "mdi-factory",
              color: "orange",
              testDetails: "Servicio CAB activo en puerto 3001",
              retestable: true,
            });

            this.testResults.network = { success: true, type: "cab_service" };
            console.log("✅ CAB: Servicio especializado disponible");
            return true;
          }
        } catch (fetchError) {
          // Fallback a servicio genérico
          try {
            const response = await fetch("http://localhost:3001/health", {
              signal: controller.signal,
              method: "GET",
            });

            clearTimeout(timeoutId);

            if (response.ok) {
              this.testedMethods.push({
                id: "network",
                name: "Impresora de Red (Compatible CAB)",
                description: "Servicio genérico compatible con CAB",
                icon: "mdi-ethernet",
                color: "success",
                testDetails: "Servicio genérico, compatible con CAB",
                retestable: true,
              });

              this.testResults.network = { success: true, type: "generic_service" };
              console.log("✅ Red: Servicio genérico compatible");
              return true;
            }
          } catch (genericError) {
            console.log("❌ Red: Sin servicios disponibles");
            return false;
          }
        }
      } catch (error) {
        console.log("❌ CAB: Error en prueba", error.message);
        return false;
      }
    },

    async testUSBMethod() {
      try {
        if (!("usb" in navigator)) return false;

        const devices = await navigator.usb.getDevices();
        if (devices.length > 0) {
          this.testedMethods.push({
            id: "usb",
            name: "Impresora USB",
            description: "Conexión directa por puerto USB",
            icon: "mdi-usb",
            color: "primary",
            testDetails: `Dispositivo disponible`,
            retestable: true,
          });
          return true;
        }
      } catch (error) {
        return false;
      }
    },

    async testSerialMethod() {
      try {
        if (!("serial" in navigator)) return false;

        const ports = await navigator.serial.getPorts();
        if (ports.length > 0) {
          this.testedMethods.push({
            id: "serial",
            name: "Puerto Serial",
            description: "Conexión por puerto COM/Serial",
            icon: "mdi-serial-port",
            color: "warning",
            testDetails: `Puerto disponible`,
            retestable: true,
          });
          return true;
        }
      } catch (error) {
        return false;
      }
    },

    async retestMethods() {
      this.retesting = true;
      await this.testAllMethods();
      this.retesting = false;

      this.$emit(
        "show-notification",
        `Pruebas completadas. ${this.testedMethods.length} métodos disponibles.`,
        "info"
      );
    },

    async retestSpecificMethod(methodId) {
      this.testingMethod = methodId;
      // Implementar re-test específico
      this.testingMethod = null;
    },

    // PROCESAMIENTO DE ETIQUETAS
    async processLabels() {
      this.printing = true;

      try {
        let zplCode = "";

        if (this.isCABMultiColumn) {
          zplCode = this.generateCABMultiColumnZPL();
        } else {
          this.selectedItems.forEach((item) => {
            const cantidad = item.cantidadImprimir || 0;
            for (let i = 0; i < cantidad; i++) {
              zplCode += this.generateZPLForItem(item);
            }
          });
        }

        let success = false;

        switch (this.selectedMethod) {
          case "download":
            success = this.downloadZPLFile(zplCode);
            break;
          case "network":
            success = await this.printViaCABNetwork(zplCode);
            break;
          case "usb":
            success = await this.printViaUSB(zplCode);
            break;
          case "serial":
            success = await this.printViaSerial(zplCode);
            break;
        }

        if (success) {
          this.$emit("labels-printed", {
            items: this.selectedItems,
            copies: this.copies,
            total: this.totalLabels,
            method: this.selectedMethod,
            format: this.currentPaperSize,
          });

          this.$emit(
            "show-notification",
            `${this.totalLabels} etiquetas ${
              this.isCABFormat ? "CAB" : ""
            } procesadas exitosamente`,
            "success"
          );

          this.closeDialog();
        }
      } catch (error) {
        console.error("Error procesando etiquetas:", error);
        this.$emit("show-notification", "Error al procesar etiquetas", "error");
      } finally {
        this.printing = false;
      }
    },

    downloadZPLFile(zplCode) {
      try {
        const blob = new Blob([zplCode], { type: "text/plain" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
        const formatName = this.isCABFormat
          ? `CAB_${this.currentPaperSize.columns}col`
          : "etiquetas";

        link.href = url;
        link.download = `${formatName}_${timestamp}.zpl`;
        link.click();

        window.URL.revokeObjectURL(url);
        return true;
      } catch (error) {
        console.error("Error descargando ZPL:", error);
        return false;
      }
    },

    async printViaCABNetwork(zplCode) {
      try {
        const endpoint =
          this.testResults.network?.type === "cab_service"
            ? "http://localhost:3001/cab/print"
            : "http://localhost:3001/print";

        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            zplCode,
            printerIP: this.methodConfigs.network.ip,
            port: this.methodConfigs.network.port,
            format: this.currentPaperSize,
          }),
        });

        return response.ok;
      } catch (error) {
        console.error("Error en impresión de red:", error);
        return false;
      }
    },

    async printViaUSB(zplCode) {
      // Implementación USB simplificada
      try {
        const devices = await navigator.usb.getDevices();
        if (devices.length === 0) {
          // Solicitar autorización
          const device = await navigator.usb.requestDevice({
            filters: [
              { vendorId: 0x0a5f }, // Zebra
              { vendorId: 0x04b4 }, // Cypress
              { vendorId: 0x14ad }, // CAB
            ],
          });
          devices.push(device);
        }

        const device = devices[0];
        await device.open();
        await device.selectConfiguration(1);
        await device.claimInterface(0);

        const encoder = new TextEncoder();
        const data = encoder.encode(zplCode);

        await device.transferOut(1, data);
        await device.close();

        return true;
      } catch (error) {
        console.error("Error en impresión USB:", error);
        return false;
      }
    },

    async printViaSerial(zplCode) {
      // Implementación Serial simplificada
      try {
        let ports = await navigator.serial.getPorts();
        if (ports.length === 0) {
          // Solicitar autorización
          const port = await navigator.serial.requestPort();
          ports.push(port);
        }

        const port = ports[0];
        await port.open({ baudRate: this.methodConfigs.serial.baudRate });

        const writer = port.writable.getWriter();
        const encoder = new TextEncoder();

        await writer.write(encoder.encode(zplCode));
        await writer.close();
        await port.close();

        return true;
      } catch (error) {
        console.error("Error en impresión Serial:", error);
        return false;
      }
    },

    downloadZPL() {
      let zplCode = "";

      if (this.isCABMultiColumn) {
        zplCode = this.generateCABMultiColumnZPL();
      } else {
        this.selectedItems.forEach((item) => {
          const cantidad = item.cantidadImprimir || 0;
          for (let i = 0; i < cantidad; i++) {
            zplCode += this.generateZPLForItem(item);
          }
        });
      }

      this.downloadZPLFile(zplCode);
    },

    // GENERACIÓN ZPL ESPECÍFICA PARA CAB - USANDO SCRIPT PROBADO
    generateCABMultiColumnZPL() {
      const paperSize = this.currentPaperSize;
      const columns = paperSize.columns; // 3 columnas

      // Preparar etiquetas
      const allLabels = [];
      this.selectedItems.forEach((item) => {
        const cantidad = item.cantidadImprimir || 0;
        for (let i = 0; i < cantidad; i++) {
          allLabels.push(item);
        }
      });

      let zplCode = "";

      // Generar en bloques de 3 etiquetas (una fila completa)
      for (let i = 0; i < allLabels.length; i += columns) {
        // Iniciar nuevo bloque ZPL
        zplCode += `^XA\n`;

        // Procesar hasta 3 etiquetas en esta fila
        for (let j = 0; j < columns && i + j < allLabels.length; j++) {
          const item = allLabels[i + j];
          const col = j; // Posición en la fila (0, 1, 2)

          // Usar las posiciones exactas que funcionan: 45, 325, 595
          const xOffset = paperSize.columnPositions
            ? paperSize.columnPositions[col]
            : [45, 325, 595][col];

          // Para cada bloque, Y siempre empieza en 0
          const yOffset = 0;

          zplCode += this.generateCABLabelContent(item, xOffset, yOffset);
        }

        // Cerrar bloque ZPL
        zplCode += `^XZ\n`;
      }

      return zplCode;
    },

    generateCABLabelContent(item, xOffset, yOffset) {
      let zpl = "";

      // SCRIPT PROBADO Y FUNCIONAL - COORDENADAS EXACTAS

      // 1. NOMBRE DEL PRODUCTO - ^FO45,15^A0N,25,20 (relativo al xOffset)
      const productName = this.truncateText(item.nombre || item.refe, 30);
      zpl += `^FO${xOffset},${yOffset + 15}^A0N,25,20^FD${productName}^FS\n`;

      // 2. REFERENCIA - ^FO45,40^A0N,15,15
      const reference = this.generateReference(item);
      zpl += `^FO${xOffset},${yOffset + 40}^A0N,15,15^FDRef: ${
        item.refe
      } / ${reference}^FS\n`;

      // 3. CÓDIGO DE BARRAS - ^FO45,60^BY1,2,40^BCN,40,N,N,N
      if (this.labelOptions.includeBarcode && item.ean) {
        zpl += `^FO${xOffset},${yOffset + 60}^BY1,2,40^BCN,40,N,N,N^FD${item.ean}^FS\n`;

        // 4. EAN (texto debajo del código de barras) - ^FO45,110^A0N,15,15
        zpl += `^FO${xOffset},${yOffset + 110}^A0N,15,15^FD${item.ean}^FS\n`;
      }

      // 5. FECHA (DD-MM-YY) - ^FO45,130^A0N,15,15
      if (this.labelOptions.includeLote) {
        const today = new Date();
        const dateStr = `${today.getDate().toString().padStart(2, "0")}-${(
          today.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}-${today.getFullYear().toString().slice(-2)}`;
        zpl += `^FO${xOffset},${yOffset + 130}^A0N,15,15^FD${dateStr}^FS\n`;
      }

      // 6. PRECIO SIN PUNTOS - ^FO45,150^A0N,50,35 (como en el script funcional)
      if (this.labelOptions.includePrice) {
        const price = this.calculateFinalPrice(item);
        const priceText = this.formatPriceForCAB(price);
        zpl += `^FO${xOffset},${yOffset + 150}^A0N,50,35^FD${priceText}^FS\n`;
      }

      return zpl;
    },

    generateZPLForItem(item) {
      const paperSize = this.currentPaperSize;

      let zpl = `^XA\n^PW${paperSize.pixelWidth}\n^LL${paperSize.pixelHeight}\n`;

      // Nombre del producto
      const productName = this.truncateText(item.nombre || item.refe, 20);
      zpl += `^FO20,20^A0N,25^FD${productName}^FS\n`;

      // Precio
      if (this.labelOptions.includePrice) {
        const price = this.calculateFinalPrice(item);
        zpl += `^FO20,60^A0B,30^FD$${this.formatPrice(price)}^FS\n`;
      }

      // Código de barras
      if (this.labelOptions.includeBarcode && item.ean) {
        zpl += `^FO20,120^BY2^BCN,60,Y,N,N^FD${item.ean}^FS\n`;
      }

      // Información adicional
      zpl += `^FO20,200^A0N,15^FD${item.marca || "N/A"} ${item.refe || "N/A"}^FS\n`;

      zpl += `^XZ\n`;
      return zpl;
    },

    // GENERACIÓN DE PREVIEW
    generatePreview() {
      if (!this.previewMode || this.selectedItems.length === 0) return;

      this.previewLabels = [];
      this.previewRows = [];

      // Generar todas las etiquetas
      let labelIndex = 1;
      this.selectedItems.forEach((item) => {
        const cantidad = item.cantidadImprimir || 0;
        for (let i = 0; i < cantidad; i++) {
          this.previewLabels.push({
            productName: item.nombre || item.refe,
            htmlPreview: this.generateHTMLPreview(item),
            index: labelIndex++,
            item: item,
          });
        }
      });

      // Si es formato multi-columna, organizar en filas
      if (this.isCABMultiColumn) {
        const columns = this.currentPaperSize.columns;

        for (let i = 0; i < this.previewLabels.length; i += columns) {
          const row = this.previewLabels.slice(i, i + columns);
          // Rellenar fila incompleta con espacios vacíos
          while (row.length < columns) {
            row.push({ isEmpty: true });
          }
          this.previewRows.push(row);
        }
      }
    },

    generateHTMLPreview(item) {
      const price = this.calculateFinalPrice(item);
      const isCompact = this.isCABFormat;

      if (isCompact) {
        // Preview específico para CAB que coincide con el ZPL
        return `
          <div style="font-family: Arial, sans-serif; height: 100%; display: flex; flex-direction: column; justify-content: flex-start; padding: 4px; box-sizing: border-box; text-align: left;">
            
            <!-- Nombre del Producto -->
            <div style="font-size: 9px; font-weight: bold; margin-bottom: 2px; line-height: 1.1;">
              ${this.truncateText(item.nombre || item.refe, 12)}
            </div>
            
            <!-- SKU/Referencia -->
            ${
              item.refe || item.sku
                ? `
              <div style="font-size: 6px; color: #666; margin-bottom: 3px; line-height: 1;">
                SKU: ${this.truncateText(item.sku || item.refe || "", 12)}
              </div>
            `
                : ""
            }
            
            ${
              this.labelOptions.includeBarcode && item.ean
                ? `
              <!-- Código de Barras -->
              <div style="text-align: center; margin: 3px 0; background: #000; color: white; padding: 1px; font-size: 6px; font-family: monospace; line-height: 1;">
                ||||| ${item.ean} |||||
              </div>
            `
                : ""
            }
            
            ${
              this.labelOptions.includePrice
                ? `
              <!-- Precio -->
              <div style="font-size: 11px; font-weight: bold; margin: 2px 0; text-align: left;">
                ${this.formatPrice(price)}
              </div>
            `
                : ""
            }
            
            ${
              this.labelOptions.includeLote
                ? `
              <!-- Información Adicional -->
              <div style="font-size: 6px; color: #666; margin-top: auto; line-height: 1;">
                ${this.truncateText(`${item.marca || ""} ${item.refe || ""}`.trim(), 10)}
              </div>
            `
                : ""
            }
            
          </div>
        `;
      } else {
        // Preview para formatos no-CAB (Zebra, etc.)
        return `
          <div style="font-family: monospace; font-size: 11px; padding: 2px; height: 100%; box-sizing: border-box;">
            <div style="font-weight: bold; margin-bottom: 2px; font-size: 10px;">
              ${this.truncateText(item.nombre || item.refe, 20)}
            </div>
            ${
              this.labelOptions.includePrice
                ? `
              <div style="font-size: 14px; font-weight: bold; margin: 2px 0;">
                ${this.formatPrice(price)}
              </div>
            `
                : ""
            }
            ${
              this.labelOptions.includeBarcode && item.ean
                ? `
              <div style="text-align: center; margin: 2px 0; background: #f0f0f0; padding: 1px; font-size: 8px;">
                ${item.ean}
              </div>
            `
                : ""
            }
            <div style="font-size: 7px; color: #666;">
              ${item.marca || "N/A"} ${item.refe || "N/A"}
            </div>
          </div>
        `;
      }
    },

    // ESTILOS PARA PREVIEW
    getLabelStyle() {
      const paperSize = this.currentPaperSize;
      const scale = this.isCABFormat ? 4 : 3;

      return {
        width: `${paperSize.width * scale}px`,
        height: `${paperSize.height * scale}px`,
        border: "1px solid #ddd",
        margin: "10px",
        backgroundColor: "white",
        position: "relative",
        display: "inline-block",
        overflow: "hidden",
      };
    },

    getCABLabelStyle() {
      const paperSize = this.currentPaperSize;
      const scale = 4;

      return {
        width: `${paperSize.width * scale}px`,
        height: `${paperSize.height * scale}px`,
        border: "1px solid #ddd",
        backgroundColor: "white",
        position: "relative",
        overflow: "hidden",
        margin: "1px", // Reducir margen para simular el espaciado real
        display: "flex",
        flexDirection: "column",
      };
    },

    getRowStyle() {
      return {
        display: "flex",
        marginBottom: "5px",
        alignItems: "flex-start",
      };
    },

    // MÉTODOS AUXILIARES
    initializeItems(items) {
      return items.map((item) => ({
        ...item,
        cantidadImprimir: item.cantidadImprimir || item.cantidad || 1,
      }));
    },

    removeItem(item) {
      const index = this.selectedItems.findIndex(
        (i) => (i.ean && i.ean === item.ean) || (i.refe && i.refe === item.refe)
      );
      if (index > -1) {
        this.selectedItems.splice(index, 1);
        this.generatePreview();
      }
    },

    getSelectedMethodName() {
      if (this.selectedMethod === "download") return "Descarga";
      const method = this.testedMethods.find((m) => m.id === this.selectedMethod);
      return method ? method.name : "Método";
    },

    getActionButtonText() {
      const count = this.totalLabels;
      const format = this.isCABMultiColumn
        ? ` (${this.currentPaperSize.columns} col.)`
        : "";

      switch (this.selectedMethod) {
        case "download":
          return `Descargar ${count} etiquetas${format}`;
        default:
          return `Procesar ${count} etiquetas${format}`;
      }
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength ? text.substring(0, maxLength - 3) + "..." : text;
    },

    formatPrice(price) {
      return parseFloat(price || 0).toFixed(2);
    },

    calculateFinalPrice(item) {
      const price = parseFloat(item.precio || 0);
      const discount = parseFloat(item.descuento || 0);
      return price * (1 - discount / 100);
    },

    // Función para generar la referencia como en el script probado
    generateReference(item) {
      // Usar el precio para generar la referencia (como en el ejemplo: 0100200000)
      const costo = parseFloat(item.costo || 0);
      const priceForRef = Math.floor(costo * 1000)
        .toString()
        .padStart(6, "0");
      return `010${priceForRef}`;
    },

    // Formatear precio para CAB (sin puntos, como "200.000")
    formatPriceForCAB(price) {
      const priceNum = parseFloat(price || 0);
      // Formatear como en el ejemplo: 200.000
      return priceNum.toLocaleString("es-CO", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
  },
};
</script>

<style scoped>
.preview-panel {
  background-color: #f5f5f5;
  min-height: 500px;
}

.preview-container {
  max-height: 450px;
  overflow-y: auto;
}

.preview-scroll {
  padding: 10px;
}

.cab-multi-column {
  display: flex;
  flex-direction: column;
}

.label-row {
  display: flex;
  margin-bottom: 5px;
  align-items: flex-start;
}

.cab-column-label {
  margin: 2px;
}

.label-preview {
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.label-info {
  position: absolute !important;
  top: -8px;
  right: -8px;
  z-index: 1;
}

.label-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

@media (max-width: 960px) {
  .preview-scroll {
    justify-content: center;
  }

  .label-preview,
  .cab-column-label {
    transform: scale(0.8);
  }

  .label-row {
    justify-content: center;
  }
}
</style>
